import type { Entity, EntityMeta } from '@backstage/catalog-model';
import type { JsonObject } from '@backstage/types';
import type { z } from 'zod';

import type { techRadarSpecSchema } from './schema';

/**
 * API version for NS custom entities
 */
export const NS_API_VERSION = 'ns.nl/v1alpha1';

/**
 * Entity kind for tech radars
 */
export const TECH_RADAR_KIND = 'TechRadar';

/**
 * Allowed values for spec.fetchMode
 */
export type TechRadarFetchMode = 'cached' | 'live';

/**
 * Strict, runtime-validated TechRadar spec inferred from `techRadarSpecSchema`.
 *
 * Used after the catalog processor has parsed an entity. Guarantees:
 * - `owner` matches `^group:`
 * - `contact` (if present) matches `^user:`
 * - `scope` is a non-empty array of non-empty strings
 * - `data` (if present) matches the strict TechRadar data shape
 * - either `dataFile` or `data` is present (refine)
 */
export type TechRadarSpec = z.infer<typeof techRadarSpecSchema>;

/**
 * Raw, json-serialisable shape of radar data as it is stored in the entity
 * spec by the catalog processor.
 *
 * The `date` fields on timeline snapshots are ISO-8601 strings here. The
 * frontend transforms them into `Date` objects via `transformRadarData`
 * before passing them to `@backstage-community/plugin-tech-radar`.
 *
 * Compatible with `JsonObject` from `@backstage/types`, so the processor
 * can assign it to `entity.spec.data` without a type assertion.
 *
 * NOTE: This persistent shape is intentionally narrower than the
 * runtime-validated shape produced by `techRadarSpecSchema`/`techRadarDataSchema`
 * (which are derived from the community parser). The persistent shape
 * whitelists only the fields we want to round-trip through `entity.spec.data`:
 * - `entry.url` is dropped (deprecated upstream)
 * - `entry.links` is dropped (legacy pre-MoveItem-API feature)
 * - `ring.description` is dropped (per-radar override not supported)
 */
export interface TechRadarRawData extends JsonObject {
  entries: TechRadarRawEntry[];
  quadrants: TechRadarRawQuadrant[];
  rings: TechRadarRawRing[];
}

export interface TechRadarRawEntry extends JsonObject {
  description?: string;
  id: string;
  key: string;
  quadrant: string;
  timeline: TechRadarRawTimelineEntry[];
  title: string;
}

export interface TechRadarRawTimelineEntry extends JsonObject {
  date: string;
  description?: string;
  moved?: number;
  ringId: string;
}

export interface TechRadarRawQuadrant extends JsonObject {
  id: string;
  name: string;
}

export interface TechRadarRawRing extends JsonObject {
  color: string;
  id: string;
  name: string;
}

/**
 * Pre-validation spec shape for TechRadar entities.
 *
 * All fields are typed as optional because the spec object is reachable from
 * code paths (e.g. catalog table column renderers, filters) before the
 * processor has had a chance to validate it. Consumers MUST guard with
 * `isTechRadarEntity` plus runtime checks before relying on individual
 * fields. For the strict, post-validation shape use `TechRadarSpec`.
 */
export interface TechRadarEntitySpec {
  /** Backstage user reference (e.g. `user:default/jane-doe`) */
  contact?: string;
  /** Radar data (populated by processor for cached mode, absent for live mode) */
  data?: TechRadarRawData;
  /** Path to the radar JSON data file (relative to entity YAML or absolute URL) */
  dataFile?: string;
  /** How radar data is loaded: cached = stored in entity spec, live = fetched at render time */
  fetchMode?: TechRadarFetchMode;
  /** Backstage group reference of the owning team (e.g. `group:default/devex`) */
  owner?: string;
  /** Multi-value scope tags (e.g. `["RG5", "RG6"]`); non-empty when valid */
  scope?: string[];
}

/**
 * TechRadar entity for NS Internal Developer Portal
 *
 * Represents a team or organisation tech radar that can be visualised
 * with the community Tech Radar plugin.
 */
export interface TechRadarEntity {
  apiVersion: typeof NS_API_VERSION;
  kind: typeof TECH_RADAR_KIND;
  metadata: EntityMeta;
  spec: TechRadarEntitySpec;
}

/**
 * Type guard to check if an entity is a TechRadar
 */
export function isTechRadarEntity(
  entity?: Entity,
): entity is Entity & TechRadarEntity {
  return (
    !!entity &&
    entity.apiVersion === NS_API_VERSION &&
    entity.kind === TECH_RADAR_KIND
  );
}
